import { KulionerMerchantReceiptSectionProps } from "../types";
import { colors, margin } from "../constants";
import { formatDateWithoutTime, formatPriceRP } from "@/app/infrastructures/misc/Utils";
import { maskRecipientPhone } from "@/app/ui/modules/receipt-v2/module";
import autoTable from "jspdf-autotable";
import { KulionerProduct } from "@/domain/entities/Kulioner";

export const renderMerchant = ({
  doc,
  x,
  y,
  pageWidth,
  data,
  isNoHeader,
  totalItem,
  totalPrice
}: KulionerMerchantReceiptSectionProps) => {
  const marginRight = 5;
  const marginTop = 2;
  const boxRadius = 1.5;

  const paddingX = 2;
  const paddingY = 4;

  const containerX = x;
  const currentY = y + marginTop;
  const boxWidth = pageWidth - marginRight - 0;
  const padding = 0.5;

  // === Hitung tinggi yang dibutuhkan ===
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(7);
  const titleLines = doc.splitTextToSize(data.name, boxWidth - 10);
  const maxTitleLines = 2;
  const displayTitleLines = titleLines.slice(0, maxTitleLines);
  const titleNeedsEllipsis = titleLines.length > maxTitleLines;
  const merchantNameHeight = doc.getTextDimensions(displayTitleLines).h;

  doc.setFont("Montserrat", "normal");
  const splitAddress = doc.splitTextToSize(data.address || "-", 75);
  const maxAddressLines = 2;
  const displayAddressLines = splitAddress.slice(0, maxAddressLines);
  const addressNeedsEllipsis = splitAddress.length > maxAddressLines;
  const addressHeight = doc.getTextDimensions(displayAddressLines).h;

  // === Merchant Name ===
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor("#333333");
  let titleY = currentY + paddingY
  if (!isNoHeader) {
    displayTitleLines.forEach((line: string, index: number) => {
      if (index === maxTitleLines - 1 && titleNeedsEllipsis) {
        // Add ellipsis to last line if truncated
        const truncatedLine = line.length > 3 ? line.slice(0, -5) + '...' : line + '...';
        doc.text(truncatedLine, containerX + padding + paddingX, titleY);
      } else {
        doc.text(line, containerX + padding + paddingX, titleY);
      }
      titleY += 3;
    });
  }

  // === Address ===
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(6);
  doc.setTextColor("#78787A");
  let addressY = currentY + paddingY + merchantNameHeight + 2;
  if (!isNoHeader) {
    displayAddressLines.forEach((line: string, index: number) => {
      if (index === maxAddressLines - 1 && addressNeedsEllipsis) {
        // Add ellipsis to last line if truncated
        const truncatedLine = line.length > 3 ? line.slice(0, -5) + '...' : line + '...';
        doc.text(truncatedLine, containerX + padding + paddingX, addressY);
      } else {
        doc.text(line, containerX + padding + paddingX, addressY);
      }
      addressY += 3;
    });
  }

  const totalItemX = pageWidth * 2 / 3

  const additionalAddressLines = displayAddressLines.length > 1 ? 2 : 0

  // === Total item ===
  doc.setFont("Montserrat", "semibold");
  doc.setFontSize(6);
  doc.setTextColor("#333333");
  if (!isNoHeader) {
    doc.text("Total item", totalItemX, currentY + paddingY + merchantNameHeight + additionalAddressLines);
  }
  const itemHeight = doc.getTextDimensions("Total item").h;
  const itemWidth = doc.getTextDimensions("Total item").w;
  
  doc.setFont("Montserrat", "semibold");
  doc.setFontSize(6);
  doc.setTextColor("#B82025");
  const totalItemWidth = doc.getTextDimensions(String(totalItem)).w;
  if (!isNoHeader) {
    doc.text(String(totalItem), totalItemX + itemWidth - totalItemWidth, currentY + paddingY + merchantNameHeight + itemHeight + additionalAddressLines + 0.5);
  }

  // === Total price ===
  const totalPriceX = containerX + padding + boxWidth

  doc.setFont("Montserrat", "semibold");
  doc.setFontSize(6);
  doc.setTextColor("#333333");
  const priceWidth = doc.getTextDimensions("Total harga").w;
  if (!isNoHeader) {
    doc.text("Total harga", totalPriceX - (priceWidth + 2), currentY + paddingY + merchantNameHeight + additionalAddressLines);
  }
  
  doc.setFont("Montserrat", "semibold");
  doc.setFontSize(6);
  doc.setTextColor("#B82025");
  const itemPrice = formatPriceRP(totalPrice)
  const itemPriceWidth = doc.getTextDimensions(itemPrice).w;
  if (!isNoHeader) {
    doc.text(itemPrice, totalPriceX - (itemPriceWidth + 2), currentY + paddingY + merchantNameHeight + itemHeight + additionalAddressLines + 0.5);
  }

  const afterAddressY = currentY + paddingY + merchantNameHeight + addressHeight + 2;

  // === Table Body Generation (tetap) ===
  const productsTableBody: any[] = [];
  data.products.forEach((product, index) => {
    doc.setFont("Montserrat", "normal");
    doc.setFontSize(6);
    const nameLines = doc.splitTextToSize(product.name, 60);
    const nameHeight = nameLines.length * 2;

    const totalHeight = Math.max(8, nameHeight + 4);
    productsTableBody.push([
      { content: "", styles: { minCellHeight: totalHeight } },
      { content: product.quantity, style: { halign: "center" }},
      formatPriceRP(product.price * product.quantity)
    ]);
  });
  const tableYStart = isNoHeader ? currentY  : afterAddressY;

  // === Table Rendering ===
  autoTable(doc, {
    theme: "plain",
    head: [["Nama Produk", { content: "Item", styles: { halign: "center"}}, { content: "Subtotal", styles: { halign: "right" }}]],
    body: productsTableBody,
    columnStyles: {
      0: { halign: "left" },
      1: { cellWidth: 13, halign: "center" },
      2: { cellWidth: 20, halign: "right" }
    },
    styles: {
      font: "Montserrat",
      fontSize: 6,
      fontStyle: "normal",
      textColor: "#333333",
      valign: "middle",
      halign: "left",
      cellPadding: { top: 1, bottom: 1, left: 2, right: 2 }
    },
    headStyles: {
      fillColor: "#F2F4F5",
      fontSize: 6,
      fontStyle: "bold",
      textColor: "#333333",
      minCellHeight: 5,
      cellPadding: { top: 1, bottom: 1, left: 2, right: 2 },
    },
    tableWidth: boxWidth,
    startY: tableYStart,
    margin: {
      left: containerX + padding,
      right: margin.right,
    },
    pageBreak: "avoid",
    didDrawCell: (item) => {
      const { column, row, cell } = item;
      if (item.section === "body" && column.index === 0) {
        if (!data.products[row.index]) return;
        const product = data.products[row.index];
        const textX = cell.x + 1.5;
        let textY = cell.y + 4;

        doc.setFont("Montserrat", "normal");
        doc.setFontSize(6);
        doc.setTextColor(colors.primary);
        const nameLines = doc.splitTextToSize(product.name, column.width - 3);
        const maxLines = 2;
        const displayLines = nameLines.slice(0, maxLines);
        const needsEllipsis = nameLines.length > maxLines;

        displayLines.forEach((line: string, index: number) => {
          if (index === maxLines - 1 && needsEllipsis) {
            // Add ellipsis to last line if truncated
            const truncatedLine = line.length > 3 ? line.slice(0, -3) + '...' : line + '...';
            doc.text(truncatedLine, textX, textY);
          } else {
            doc.text(line, textX, textY);
          }
          textY += 2;
        });
      }
    },
  });

  const finalY = doc.lastAutoTable.finalY;


  doc.setDrawColor("#E6E6E6");
  doc.setLineWidth(0.5);
  doc.roundedRect(
    containerX + padding,
    currentY,
    boxWidth,
    finalY - currentY + paddingY,
    boxRadius,
    boxRadius,
    "S"
  );
};
